import moment from "moment-timezone";
import { Null, Optional, Record as RRecord, Static, Union } from "runtypes";

import {
  DATE_FORMAT_NO_TIME,
  DateAndTime,
  DateOnly,
  DatetimeValue,
  RelativeDateOperation,
  RelativeDatetimeOffsetValue,
} from "./datetimeType.js";
import { TableFiltersDefinition } from "./display-table/index.js";
import { assertNever } from "./errors.js";
import { FilterGroup, FilterTypeLiteral } from "./filter/index.js";
import {
  CellId,
  DisplayTableConfigId,
  DisplayTableSessionStateId,
  FilterCellId,
  FilterCellSessionStateId,
  SharedFilterId,
  SharedFilterSessionStateId,
} from "./idTypeBrands.js";
import { ParameterInputType, ParameterOptions } from "./parameterType.js";
import {
  DatetimeValueTuple,
  ParameterValueContents,
} from "./parameterValueType.js";
import { ParameterQueryParamValue } from "./queryParamTypes.js";
import {
  SharedFilterAutoLinks,
  SharedFilterManualLinks,
  SharedFilterPredicateOp,
} from "./sharedFilterTypes.js";

export const SESSION_STATE_PREFIX = `_session_state`;
export const SHARED_FILTER_SESSION_STATE_PREFIX = `${SESSION_STATE_PREFIX}_shared_filter`;
export const DISPLAY_TABLE_SESSION_STATE_PREFIX = `${SESSION_STATE_PREFIX}_display_table`;
export const FILTER_CELL_SESSION_STATE_PREFIX = `${SESSION_STATE_PREFIX}_filter_cell`;

function formatDateTimeValue(paramValue: DatetimeValue): string {
  let datetimeString = "";
  if (RelativeDatetimeOffsetValue.guard(paramValue)) {
    datetimeString = `${paramValue.amount} ${paramValue.unit}${
      paramValue.amount === 1 ? "" : "s"
    } ${
      paramValue.operation === RelativeDateOperation.AGO ? "ago" : "from now"
    } ${paramValue.timezone}`;
  } else if (DateAndTime.guard(paramValue)) {
    datetimeString = `${paramValue.isoDateString} ${paramValue.timezoneName}`;
  } else if (DateOnly.guard(paramValue)) {
    datetimeString = moment(paramValue.dateString).format(DATE_FORMAT_NO_TIME);
  } else {
    assertNever(paramValue, paramValue);
  }
  return datetimeString;
}

export const serializeInputName = (paramName: string): string => {
  return `_${paramName}`;
};

export const serializeSharedFilterName = (
  filterId: SharedFilterSessionStateId,
): string => {
  return `${SHARED_FILTER_SESSION_STATE_PREFIX}_${filterId}`;
};

export const serializeDisplayTableConfigName = (
  displayTableId: DisplayTableSessionStateId,
): string => {
  return `${DISPLAY_TABLE_SESSION_STATE_PREFIX}_${displayTableId}`;
};

export const serializeFilterCellName = (
  filterCellId: FilterCellSessionStateId,
): string => {
  return `${FILTER_CELL_SESSION_STATE_PREFIX}_${filterCellId}`;
};

export const serializeInputStates = (
  paramValue: ParameterValueContents,
  inputType: ParameterInputType,
):
  | {
      value: ParameterQueryParamValue;
    }
  | {
      error: string;
    } => {
  switch (inputType) {
    case ParameterInputType.DATE:
      if (paramValue) {
        if (DatetimeValue.guard(paramValue)) {
          return {
            value: formatDateTimeValue(paramValue),
          };
        } else if (DatetimeValueTuple.guard(paramValue)) {
          return {
            value: `${formatDateTimeValue(paramValue[0])} - ${formatDateTimeValue(
              paramValue[1],
            )}`,
          };
        }
        return {
          error: "Invalid date value for param",
        };
      }
      break;
    case ParameterInputType.FILE_UPLOAD:
      // These are unsupported input types
      return {
        error: `Cannot generate query param value for param of type ${inputType}`,
      };
    case ParameterInputType.BUTTON:
    case ParameterInputType.TEXT_INPUT:
    case ParameterInputType.NUMERIC_INPUT:
    case ParameterInputType.DROPDOWN:
    case ParameterInputType.TABLE:
    case ParameterInputType.SLIDER:
    case ParameterInputType.CHECKBOX:
    case ParameterInputType.MULTISELECT:
      // For all other input types just return the value as-is
      break;
    default:
      assertNever(inputType, inputType);
  }

  if (ParameterQueryParamValue.guard(paramValue)) {
    return {
      value: paramValue,
    };
  }

  return {
    error: `Invalid value for param of type ${inputType}`,
  };
};

function serializeBase64(value: unknown): string {
  // We're using btoa here because FE code does not include a buffer implementation unless we polyfill
  return (
    btoa(JSON.stringify(value))
      // Strip out the padding from the base64 encoding because the `=` characters trip
      // up Cloudflare's WAF when used in query parameters. Node decoder correctly
      // decodes the string without the padding.
      .replaceAll("=", "")
  );
}

// This runtype is used when encoding the shared filter into shareable app session links
export const RawSharedFilterSessionState = RRecord({
  operator: Optional(SharedFilterPredicateOp),
  options: Union(Optional(ParameterOptions), Null),
  autoLinks: Optional(SharedFilterAutoLinks),
  manualLinks: Optional(SharedFilterManualLinks),
  value: Optional(ParameterValueContents),
  filterId: Optional(SharedFilterId),
});
export type RawSharedFilterSessionState = Static<
  typeof RawSharedFilterSessionState
>;

export const serializeSharedFilterStates = (
  sharedFilterState: RawSharedFilterSessionState,
): string => {
  return serializeBase64(sharedFilterState);
};

// This runtype is used when encoding the display table filter into shareable app
// session links
export const RawDisplayTableSessionState = RRecord({
  displayTableConfigId: DisplayTableConfigId,
  cellId: CellId,
  additionalFilters: Optional(TableFiltersDefinition),
});
export type RawDisplayTableSessionState = Static<
  typeof RawDisplayTableSessionState
>;
export const serializeDisplayTableStates = (
  displayTableState: RawDisplayTableSessionState,
): string => {
  return serializeBase64(displayTableState);
};

// This runtype is used when encoding the display table filter into shareable app
// session links
export const RawFilterCellSessionState = RRecord({
  cellId: CellId,
  filterCellId: FilterCellId,
  filtersOverride: Optional(FilterGroup),
  filterTypeOverride: Optional(FilterTypeLiteral),
});
export type RawFilterCellSessionState = Static<
  typeof RawFilterCellSessionState
>;

export const serializeFilterCellStates = (
  filterState: RawFilterCellSessionState,
): string => {
  return serializeBase64(filterState);
};
